import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { TooltipContent } from '../../utils/metricAnnotations';
import { useTooltipCopy } from '../../contexts/TooltipCopyContext';

/**
 * TendTooltip — the one glass tooltip affordance, the FeedbackButton
 * pattern applied to explanations: a surface wraps its cell in this and
 * names what it's explaining (usually via the metricAnnotations registry);
 * chrome, hover behavior, positioning and stacking live here and nowhere
 * else.
 *
 * - Title + subtext over the tend-card-chrome frost (the feedback panel's
 *   material), small and client-facing.
 * - Pure hover/focus — appears immediately, no click, no help cursor.
 * - Close is GRACED (~350ms) so the pointer can travel into the bubble;
 *   hovering the bubble holds it open.
 * - Registry-keyed copy is editable IN SITU by universal admins
 *   (SuperAdmin / Customer Success): pencil → the rendered title and
 *   subtext themselves become contentEditable exactly as they stand —
 *   zero visual shift (top edge re-anchored so Save / Cancel / Reset grow
 *   strictly downward; no outline; the pencil keeps its slot) — the
 *   feedback-editing convention. Overrides store universally
 *   (tendv2_settings) and render for everyone; editing pins the bubble
 *   open.
 * - Portaled to <body> at the feedback panel's z-band, so table overflow
 *   and page stacking contexts can never clip or bury it.
 */

interface TendTooltipProps {
  /** Registry entry (preferred) — utils/metricAnnotations.ts. */
  content?: TooltipContent;
  /** One-off copy when the registry doesn't apply (not editable). */
  title?: string;
  subtext?: string;
  /** Classes for the inline anchor wrapper (carry the cell's classes here). */
  className?: string;
  children: React.ReactNode;
}

const TOOLTIP_WIDTH = 250;
const VIEWPORT_MARGIN = 12;
const ANCHOR_GAP = 8;
/** Hover-out grace: time to travel from the anchor into the bubble. */
const CLOSE_DELAY_MS = 350;
const MAX_TITLE = 80;
const MAX_SUBTEXT = 500;

/** In-situ editing keeps the text EXACTLY as rendered — no outline (the
 * browser's focus ring included), no background, nothing that could shift
 * or restyle a single glyph. The caret and the Save/Cancel row are the
 * editing signal. */
const EDITABLE_STYLE: React.CSSProperties = {
  cursor: 'text',
  outline: 'none',
};

interface BubblePosition {
  left: number;
  top?: number;
  bottom?: number;
}

const TendTooltip: React.FC<TendTooltipProps> = ({ content, title, subtext, className, children }) => {
  const anchorRef = useRef<HTMLSpanElement>(null);
  const closeTimerRef = useRef<number | null>(null);
  const [position, setPosition] = useState<BubblePosition | null>(null);
  const [editing, setEditing] = useState(false);
  const [draftValid, setDraftValid] = useState(true);
  const [saving, setSaving] = useState(false);
  const titleRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const bubbleRef = useRef<HTMLDivElement>(null);

  const { overrides, canEdit, save, reset } = useTooltipCopy();

  const override = content?.key ? overrides[content.key] : undefined;
  const heading = override?.title ?? content?.title ?? title;
  const body = override?.subtext ?? content?.subtext ?? subtext;
  const editable = canEdit && !!content?.key;

  const cancelPendingClose = useCallback(() => {
    if (closeTimerRef.current !== null) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const open = useCallback(() => {
    cancelPendingClose();
    const rect = anchorRef.current?.getBoundingClientRect();
    if (!rect) return;
    let left = (rect.left + rect.right) / 2 - TOOLTIP_WIDTH / 2;
    left = Math.max(VIEWPORT_MARGIN, Math.min(left, window.innerWidth - TOOLTIP_WIDTH - VIEWPORT_MARGIN));
    // Above the anchor by default (bottom-anchored so any height grows
    // upward); below when the anchor hugs the top of the viewport.
    if (rect.top > 160) {
      setPosition({ left, bottom: window.innerHeight - rect.top + ANCHOR_GAP });
    } else {
      setPosition({ left, top: rect.bottom + ANCHOR_GAP });
    }
  }, [cancelPendingClose]);

  const scheduleClose = useCallback(() => {
    if (editing) return; // editing pins the bubble open
    cancelPendingClose();
    closeTimerRef.current = window.setTimeout(() => {
      closeTimerRef.current = null;
      setPosition(null);
    }, CLOSE_DELAY_MS);
  }, [editing, cancelPendingClose]);

  useEffect(() => cancelPendingClose, [cancelPendingClose]);

  // Scrolling under an open tooltip strands the bubble — dismiss (unless
  // mid-edit, where losing the drafts would be worse than a stray bubble).
  useEffect(() => {
    if (!position || editing) return;
    const dismiss = () => { cancelPendingClose(); setPosition(null); };
    window.addEventListener('scroll', dismiss, { capture: true, passive: true });
    return () => window.removeEventListener('scroll', dismiss, { capture: true } as any);
  }, [position, editing, cancelPendingClose]);

  const startEdit = () => {
    // Re-anchor to the bubble's current TOP edge so the extra Save/Cancel
    // row grows strictly downward — the text the editor is looking at
    // must not move a pixel when editing starts.
    const rect = bubbleRef.current?.getBoundingClientRect();
    if (rect) setPosition({ left: rect.left, top: rect.top });
    setDraftValid(true);
    setEditing(true);
  };

  // Focus the title once the nodes flip to contentEditable
  useEffect(() => {
    if (editing) titleRef.current?.focus();
  }, [editing]);

  const readDrafts = () => ({
    title: titleRef.current?.textContent?.trim() || '',
    subtext: bodyRef.current?.textContent?.trim() || '',
  });

  const checkDrafts = () => {
    const { title: t, subtext: s } = readDrafts();
    setDraftValid(!!t && !!s && t.length <= MAX_TITLE && s.length <= MAX_SUBTEXT);
  };

  const saveEdit = async () => {
    const { title: t, subtext: s } = readDrafts();
    if (!content?.key || !t || !s || t.length > MAX_TITLE || s.length > MAX_SUBTEXT || saving) return;
    setSaving(true);
    try {
      await save(content.key, { title: t, subtext: s });
      setEditing(false);
    } catch {
      // keep the editor open so the drafts aren't lost on a failed save
    } finally {
      setSaving(false);
    }
  };

  const resetEdit = async () => {
    if (!content?.key || saving) return;
    setSaving(true);
    try {
      await reset(content.key);
      setEditing(false);
    } catch { /* keep editor open */ } finally {
      setSaving(false);
    }
  };

  if (!heading && !body) return <>{children}</>;

  return (
    <span
      ref={anchorRef}
      className={className}
      // Hover must reach the anchor even under a pointer-events:none host
      // (the content card's pass-through analytics bar).
      style={{ pointerEvents: 'auto' }}
      onMouseEnter={open}
      onMouseLeave={scheduleClose}
      onFocus={open}
      onBlur={scheduleClose}
    >
      {children}
      {position && createPortal(
        <div
          ref={bubbleRef}
          role="tooltip"
          className="tend-card-chrome rounded-xl border border-white/15 shadow-2xl px-3.5 py-2.5"
          style={{
            position: 'fixed',
            width: TOOLTIP_WIDTH,
            zIndex: 9800,
            // The bubble is mouse-transparent for regular users (it can
            // never trap clicks); editors can enter it to reach the pencil.
            pointerEvents: editable ? 'auto' : 'none',
            ...position,
          }}
          onMouseEnter={cancelPendingClose}
          onMouseLeave={scheduleClose}
          // Interacting with the bubble must not read as an outside-click
          // to the host surface (Analytics panel etc.) — the FeedbackPanel
          // convention for body-portaled popovers.
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
          // Focus inside the bubble (the contentEditable nodes) would
          // otherwise propagate up the REACT tree to the anchor's
          // onFocus={open} and re-run positioning, undoing the edit-mode
          // top-freeze. Portal events bubble through React, not the DOM.
          onFocus={(e) => e.stopPropagation()}
          onBlur={(e) => e.stopPropagation()}
        >
          {/* In-situ editing: the rendered title/subtext THEMSELVES become
              editable (keyed remount resets contentEditable DOM on toggle,
              so Cancel always restores the real copy). */}
          <div
            onKeyDown={editing ? (e) => {
              if (e.key === 'Escape') { e.stopPropagation(); setEditing(false); }
            } : undefined}
          >
            <div className="flex items-start justify-between gap-2">
              <div
                key={editing ? 'title-edit' : 'title-view'}
                ref={titleRef}
                contentEditable={editing}
                suppressContentEditableWarning
                onInput={checkDrafts}
                onKeyDown={editing ? (e) => {
                  // Title is single-line — Enter moves on to the subtext
                  if (e.key === 'Enter') { e.preventDefault(); bodyRef.current?.focus(); }
                } : undefined}
                className="tend-micro flex-1 min-w-0"
                style={editing ? EDITABLE_STYLE : undefined}
              >
                {heading}
              </div>
              {editable && (
                <button
                  type="button"
                  onClick={startEdit}
                  className={`text-white/35 hover:text-white transition-colors flex-none ${editing ? 'invisible' : ''}`}
                  title="Edit this tooltip (shown to all users)"
                  aria-label="Edit tooltip"
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                </button>
              )}
            </div>
            {(body || editing) && (
              <div
                key={editing ? 'body-edit' : 'body-view'}
                ref={bodyRef}
                contentEditable={editing}
                suppressContentEditableWarning
                onInput={checkDrafts}
                className="text-xs text-white/85 leading-snug"
                style={{
                  marginTop: heading ? 5 : 0,
                  whiteSpace: 'pre-wrap',
                  ...(editing ? EDITABLE_STYLE : null),
                }}
              >
                {body}
              </div>
            )}
            {editing && (
              <div className="flex items-center gap-3 mt-2.5">
                <button
                  type="button"
                  onClick={saveEdit}
                  disabled={saving || !draftValid}
                  className="text-[11px] text-white/80 hover:text-white disabled:opacity-40"
                >
                  {saving ? 'Saving…' : 'Save'}
                </button>
                <button
                  type="button"
                  onClick={() => setEditing(false)}
                  disabled={saving}
                  className="text-[11px] text-white/50 hover:text-white/80"
                >
                  Cancel
                </button>
                {override && (
                  <button
                    type="button"
                    onClick={resetEdit}
                    disabled={saving}
                    className="ml-auto text-[11px] text-white/40 hover:text-white/70"
                    title="Return to TEND's shipped wording"
                  >
                    Restore TEND default
                  </button>
                )}
              </div>
            )}
          </div>
        </div>,
        document.body,
      )}
    </span>
  );
};

export default TendTooltip;
